import React, { useState, useEffect, useCallback } from "react";
import axiosInstance from "../../../axiosInstance";
import "./Department.scss";
import IconButton from "../../../components/IconButton/IconButton";
import ToggleButton from "../../../components/ToggleButton/ToggleButton";
import ButtonWithIcon from "../../../components/ButtonwithIcon/ButtonWithIcon";
import { toast } from "react-toastify";
import WarningPopup from "../../../components/WarningPopup/WarningPopup";
import GeneralTable from "../../../components/Table/GeneralTable";
import ColoredCheckbox from "../../../components/ColoredCheckbox/ColoredCheckbox";

const Department = ({ markPageAsCompleted }) => {
  const [departments, setDepartments] = useState([]);
  const [departmentName, setDepartmentName] = useState("");
  const [editingDepartmentId, setEditingDepartmentId] = useState(null);
  const [error, setError] = useState("");
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [bulkUpdateEnabled, setBulkUpdateEnabled] = useState(false);
  const [editedDepartmentNames, setEditedDepartmentNames] = useState({});
  const [bulkDeleteEnabled, setBulkDeleteEnabled] = useState(true);
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [showWarningPopup, setShowWarningPopup] = useState(false);

  // Manager dropdown state
  const [managers, setManagers] = useState([]);
  const [selectedManagerId, setSelectedManagerId] = useState("");
  const [managersLoading, setManagersLoading] = useState(false);

  const fetchDepartments = useCallback(async () => {
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_BASE_URL}/departments/all`,
      );
      const departmentsData = response.data?.data || [];

      if (departmentsData.length > 0) {
        setDepartments(
          departmentsData.map((department) => ({
            id: department.departmentId,
            name: department.departmentName || "No Name",
            managerId: department.departmentManagerId || null,
            managerName: department.departmentManagerName || "—",
            status: department.status || "ENABLED",
          })),
        );
        markPageAsCompleted("Department");
      } else {
        setDepartments([]);
        console.error("No departments found or empty data:", response.data);
      }
    } catch (error) {
      console.error("Error fetching departments:", error);
    }
  }, [markPageAsCompleted]);

  const fetchManagers = useCallback(async () => {
    setManagersLoading(true);
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_EMPLOYEE_URL}/employees/manager/list`,
      );
      const managersData = response.data?.data || [];
      setManagers(managersData);
    } catch (error) {
      console.error("Error fetching managers:", error);
      toast.error("Failed to load managers list.");
    } finally {
      setManagersLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDepartments();
  }, [fetchDepartments]);

  // Fetch managers once on mount (needed whenever the form is shown)
  useEffect(() => {
    fetchManagers();
  }, [fetchManagers]);

  const handleAddDepartment = async () => {
    try {
      const requestBody = {
        deptName: departmentName,
        empId: selectedManagerId ? Number(selectedManagerId) : undefined,
        status: "ENABLED",
        companyId: Number(process.env.REACT_APP_COMPANY_ID),
      };
      if (editingDepartmentId) {
        await axiosInstance.put(
          `${process.env.REACT_APP_API_BASE_URL}/departments/${editingDepartmentId}`,
          { ...requestBody, deptId: editingDepartmentId },
        );
      } else {
        await axiosInstance.post(
          `${process.env.REACT_APP_API_BASE_URL}/departments`,
          requestBody,
        );
      }
      await fetchDepartments();
      setShowCreateForm(false);
      setDepartmentName("");
      setSelectedManagerId("");
      markPageAsCompleted("Department");
      toast.success(
        editingDepartmentId
          ? "Department updated successfully."
          : "Department created successfully.",
      );
      setEditingDepartmentId(null);
    } catch (error) {
      if (error.response) {
        switch (error.response.status) {
          case 400:
            setError("Invalid data. Please check the input fields.");
            break;
          case 401:
            setError("Unauthorized. Please log in.");
            break;
          case 403:
            setError(
              "Access denied. You don't have permission to perform this action.",
            );
            break;
          case 404:
            setError("Department not found.");
            break;
          case 500:
            setError("Server error. Please try again later.");
            break;
          default:
            setError("An unexpected error occurred. Please try again.");
        }
      } else {
        setError(
          "Network error. Please check your internet connection and try again.",
        );
      }
      console.error("Error submitting Department:", error);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axiosInstance.delete(
        `${process.env.REACT_APP_API_BASE_URL}/departments/${id}`,
      );
      setDepartments(departments.filter((department) => department.id !== id));
      toast.success("Department deleted successfully.");
    } catch (error) {
      console.error("Error deleting department:", error);
    }
  };

  const handleEdit = (id) => {
    const departmentToEdit = departments.find(
      (department) => department.id === id,
    );
    setDepartmentName(departmentToEdit.name);
    setSelectedManagerId(
      departmentToEdit.managerId ? String(departmentToEdit.managerId) : "",
    );
    window.scrollTo({ top: 0, behavior: "smooth" });
    setEditingDepartmentId(id);
    setShowCreateForm(true);
  };

  const handleToggleStatus = async (departmentId, currentStatus) => {
    const newStatus = currentStatus === "Enabled" ? "Disabled" : "Enabled";
    const endpoint = `${process.env.REACT_APP_API_BASE_URL}/departments/${departmentId}/${
      newStatus === "Enabled" ? "enable" : "disable"
    }`;

    try {
      await axiosInstance.put(endpoint);
      setDepartments((prevDepartments) =>
        prevDepartments.map((department) =>
          department.id === departmentId
            ? { ...department, status: newStatus }
            : department,
        ),
      );
      markPageAsCompleted("Department");
    } catch (error) {
      console.error(`Error changing department status to ${newStatus}:`, error);
    }
  };

  const handleBulkUpdateClick = async () => {
    if (bulkUpdateEnabled) {
      await handleBulkUpdateSubmit();
    } else {
      setBulkUpdateEnabled(true);
      const initialNames = {};
      departments.forEach((department) => {
        initialNames[department.id] = department.name;
      });
      setEditedDepartmentNames(initialNames);
    }
  };

  const handleBulkNameChange = (id, value) => {
    setEditedDepartmentNames({
      ...editedDepartmentNames,
      [id]: value,
    });
  };

  const handleBulkUpdateSubmit = async () => {
    for (const [id, name] of Object.entries(editedDepartmentNames)) {
      if (name.trim() !== "") {
        const requestBody = {
          deptName: name,
          status: "ENABLED",
          companyId: 1,
        };
        await axiosInstance.put(
          `${process.env.REACT_APP_API_BASE_URL}/departments/${id}`,
          requestBody,
        );
      }
    }
    toast.success("Bulk update submitted successfully");
    await fetchDepartments();
    setBulkUpdateEnabled(false);
  };

  const handleBulkDeleteClick = () => {
    if (selectedDepartments.length === 0) {
      toast.error("Please select departments to delete.");
      return;
    }
    setShowWarningPopup(true);
  };

  const handleBulkDeleteSubmit = async () => {
    try {
      await axiosInstance.delete(
        `${process.env.REACT_APP_API_BASE_URL}/departments`,
        { data: selectedDepartments },
      );
      await fetchDepartments();
      setBulkDeleteEnabled(false);
      setSelectedDepartments([]);
      toast.success("Selected departments deleted successfully.");
    } catch (error) {
      console.error("Error deleting departments:", error);
    } finally {
      setShowWarningPopup(false);
    }
  };

  const handleCheckboxChange = (departmentId, isChecked) => {
    setSelectedDepartments((prevSelected) => {
      if (isChecked) {
        return prevSelected.includes(departmentId)
          ? prevSelected
          : [...prevSelected, departmentId];
      } else {
        return prevSelected.filter((id) => id !== departmentId);
      }
    });
  };

  const handleSelectAll = (e) => {
    const isChecked = e.target.checked;
    if (isChecked) {
      setSelectedDepartments(departments.map((dept) => dept.id));
    } else {
      setSelectedDepartments([]);
    }
  };

  const isAllSelected =
    departments.length > 0 &&
    departments.every((dept) => selectedDepartments.includes(dept.id));

  const getTableColumns = () => [
    {
      header: (
        <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
          <ColoredCheckbox
            checked={isAllSelected}
            onChange={handleSelectAll}
            color="green"
          />
          <span>Departments</span>
        </div>
      ),
      accessor: "nameDisplay",
    },
    { header: "Manager", accessor: "managerName" },
    { header: "Status", accessor: "status" },
    { header: "Actions", accessor: "actions" },
  ];

  const getFormattedTableData = () => {
    return departments.map((department) => {
      const nameValue = department.name;
      return {
        id: department.id,
        name: nameValue,
        nameDisplay: (
          <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
            {bulkDeleteEnabled && (
              <ColoredCheckbox
                checked={selectedDepartments.includes(department.id)}
                onChange={(e) =>
                  handleCheckboxChange(department.id, e.target.checked)
                }
                color="green"
              />
            )}
            {bulkUpdateEnabled ? (
              <input
                type="text"
                value={editedDepartmentNames[department.id] || department.name}
                className="user-input"
                onChange={(e) =>
                  handleBulkNameChange(department.id, e.target.value)
                }
              />
            ) : (
              <span>{department.name}</span>
            )}
          </div>
        ),
        status: (
          <ToggleButton
            isOn={department.status === "Enabled"}
            onToggle={() =>
              handleToggleStatus(department.id, department.status)
            }
            status={department.status === "Disabled"}
          />
        ),
        actions: (
          <div className="action-buttons">
            <IconButton
              icon="iconamoon:edit-light"
              className="department-edit"
              onClick={() => handleEdit(department.id)}
              variant="edit"
              title="Edit Department"
            />
            <IconButton
              icon="fluent:delete-28-regular"
              className="department-delete"
              onClick={() => handleDelete(department.id)}
              variant="delete"
              title="Delete Department"
            />
          </div>
        ),
        managerName: <span>{department.managerName}</span>,
        searchableText: `${nameValue} ${department.managerName} ${department.status}`,
      };
    });
  };

  return (
    <div className="department-container">
      <h2 className="display6">Department</h2>
      {error && <div className="error-message">{error}</div>}
      <div className="department-header">
        <button
          className="create-role-button"
          onClick={() => {
            if (showCreateForm) {
              setShowCreateForm(false);
            } else {
              setDepartmentName("");
              setSelectedManagerId("");
              setShowCreateForm(true);
              setEditingDepartmentId(null);
            }
          }}
          style={
            showCreateForm ? { backgroundColor: "#ff6692", color: "#fff" } : {}
          }
        >
          {!showCreateForm && (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              className="icon"
              width="16"
              height="16"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 4v16m8-8H4"
              />
            </svg>
          )}
          {showCreateForm ? "Close Department form" : "Create Department"}
        </button>
        <button className="create-role-button" onClick={handleBulkUpdateClick}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="currentColor"
            className="icon"
            width="16"
            height="16"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M4 6h16M4 12h16m-7 6h7"
            />
          </svg>
          {bulkUpdateEnabled ? "Submit Bulk Update" : "Enable Bulk Update"}
        </button>
        <div className="bulk-delete-wrapper">
          <ButtonWithIcon
            icon="fluent:delete-28-regular"
            className="cancel-button"
            text={`Bulk Delete (${selectedDepartments.length})`}
            onClick={handleBulkDeleteClick}
            disabled={selectedDepartments.length === 0}
          />
        </div>
      </div>

      {showCreateForm && (
        <div className="w-full p-3 bg-white rounded-2xl shadow-lg border border-gray-100 space-y-10">
          <h3 className="text-xl font-semibold text-gray-900 mb-3 ml-2 mt-2">
            {editingDepartmentId ? "Edit Department" : "Create New Department"}
          </h3>

          <div
            className="department-form m-0 ml-2"
            style={{
              display: "flex",
              alignItems: "flex-end",
              gap: "12px",
              flexWrap: "wrap",
            }}
          >
            <div
              style={{ display: "flex", flexDirection: "column", gap: "4px" }}
            >
              <label className="block font-medium text-sm text-gray-700">
                Department Name
              </label>
              <input
                type="text"
                value={departmentName}
                onChange={(e) => setDepartmentName(e.target.value)}
                placeholder="Eg: Admin"
              />
            </div>

            <div
              style={{ display: "flex", flexDirection: "column", gap: "4px" }}
            >
              <label className="block font-medium text-sm text-gray-700">
                Manager
              </label>
              <select
                value={selectedManagerId}
                onChange={(e) => setSelectedManagerId(e.target.value)}
                className="user-input"
                disabled={managersLoading}
                style={{ minWidth: "200px" }}
              >
                <option value="">
                  {managersLoading
                    ? "Loading managers..."
                    : "Select a manager (optional)"}
                </option>
                {managers.map((manager) => (
                  <option key={manager.empId} value={manager.empId}>
                    {manager.firstName} {manager.lastName} —{" "}
                    {manager.companyEmail}
                  </option>
                ))}
              </select>
            </div>

            <ButtonWithIcon
              icon="tdesign:send"
              text={editingDepartmentId ? "Update" : "Submit"}
              onClick={handleAddDepartment}
              className="submit-button"
              type="submit"
            />
          </div>
        </div>
      )}

      <GeneralTable
        columns={getTableColumns()}
        data={getFormattedTableData()}
        title="Departments"
        selectedCount={selectedDepartments.length}
        showPagination={true}
        initialPageSize={10}
      />
      <WarningPopup
        isOpen={showWarningPopup}
        onClose={() => setShowWarningPopup(false)}
        onDecline={() => setShowWarningPopup(false)}
        onAccept={handleBulkDeleteSubmit}
        title="Confirm Bulk Delete"
        message={`Are you sure you want to delete ${selectedDepartments.length} department(s)? This action cannot be undone.`}
        acceptText="Yes, Delete"
        declineText="Cancel"
      />
    </div>
  );
};

export default Department;
